import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import {
  ensureUserExists,
  sendFriendRequest as sendFriendRequestService,
  getIncomingFriendRequests,
  acceptFriendRequest as acceptFriendRequestService,
  rejectFriendRequest as rejectFriendRequestService,
  getFriendsList,
  getOrCreateConversation,
  sendMessage as sendMessageService,
  getMessages,
  markMessagesAsRead,
  subscribeToMessages,
  subscribeToFriendRequests,
  unsubscribeChannel
} from '../services/supabaseFriendChat';

const SupabaseContext = createContext({});

export const useSupabase = () => {
  const context = useContext(SupabaseContext);
  if (!context) {
    throw new Error('useSupabase must be used within a SupabaseProvider');
  }
  return context;
};

export const SupabaseProvider = ({ children }) => {
  const [friendRequestsChannel, setFriendRequestsChannel] = useState(null);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(false);

  /**
   * Initialize user in Supabase (lazy upsert)
   * Call this before any friend/chat operation
   */
  const initializeSupabaseUser = useCallback(async (mongoUser) => {
    if (!mongoUser?.id || !mongoUser?.full_name) {
      return { success: false, error: 'Invalid user data' };
    }

    const result = await ensureUserExists(
      mongoUser.id,
      mongoUser.full_name,
      mongoUser.avatar_url || null
    );

    return result;
  }, []);

  /**
   * Send a friend request
   */
  const sendFriendRequest = useCallback(async (mongoUser, receiverId) => {
    if (!mongoUser?.id) {
      toast.error('You must be logged in');
      return { success: false, error: 'Not logged in' };
    }

    // Ensure sender exists in Supabase
    await initializeSupabaseUser(mongoUser);

    const result = await sendFriendRequestService(mongoUser.id, receiverId);

    if (result.success) {
      toast.success('Friend request sent!');
    } else {
      toast.error(result.error?.message || result.error || 'Failed to send friend request');
    }

    return result;
  }, [initializeSupabaseUser]);

  /**
   * Fetch incoming friend requests
   */
  const fetchFriendRequests = useCallback(async (userId) => {
    if (!userId) return { success: false, error: 'User ID required' };

    setLoading(true);
    const result = await getIncomingFriendRequests(userId);
    setLoading(false);

    if (result.success) {
      setPendingRequests(result.data);
    }

    return result;
  }, []);

  /**
   * Accept a friend request
   */
  const acceptFriendRequest = useCallback(async (mongoUser, requestId, requesterId, requesterName = 'User') => {
    if (!mongoUser?.id) {
      toast.error('You must be logged in');
      return { success: false, error: 'Not logged in' };
    }

    // Pass both user names so the service can ensure both exist in Supabase
    const result = await acceptFriendRequestService(
      requestId, 
      mongoUser.id, 
      requesterId,
      mongoUser.full_name || 'User',
      requesterName
    );

    if (result.success) {
      // Award credits to both users via backend API
      try {
        const token = localStorage.getItem('token');
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        
        const creditResponse = await fetch(`${API}/friends/award-connection-credits`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            acceptor_id: mongoUser.id,
            requester_id: requesterId
          })
        });
        
        const creditResult = await creditResponse.json();
        
        if (creditResult.success && creditResult.credits_awarded_acceptor > 0) {
          toast.success(`🎉 Connection made! +${creditResult.credits_awarded_acceptor} AI Credit earned!`, {
            duration: 4000,
            icon: '🪙'
          });
        } else if (creditResult.success) {
          toast.success('Friend request accepted!');
        }
      } catch (creditError) {
        console.error('Error awarding connection credits:', creditError);
        toast.success('Friend request accepted!');
      }
      
      // Refresh lists
      fetchFriendRequests(mongoUser.id);
      fetchFriends(mongoUser.id);
    } else {
      toast.error(result.error?.message || result.error || 'Failed to accept friend request');
    }

    return result;
  }, []);

  /**
   * Reject a friend request
   */
  const rejectFriendRequest = useCallback(async (mongoUser, requestId) => {
    if (!mongoUser?.id) {
      toast.error('You must be logged in');
      return { success: false, error: 'Not logged in' };
    }

    const result = await rejectFriendRequestService(requestId);

    if (result.success) {
      toast.success('Friend request declined');
      // Refresh requests list
      fetchFriendRequests(mongoUser.id);
    } else {
      toast.error(result.error?.message || result.error || 'Failed to decline friend request');
    }

    return result;
  }, []);

  /**
   * Fetch friends list
   */
  const fetchFriends = useCallback(async (userId) => {
    if (!userId) return { success: false, error: 'User ID required' };

    setLoading(true);
    const result = await getFriendsList(userId);
    setLoading(false);

    if (result.success) {
      setFriends(result.data);
    }

    return result;
  }, []);

  /**
   * Open or create a conversation with a friend
   */
  const openConversation = useCallback(async (mongoUser, friendId) => {
    if (!mongoUser?.id) {
      toast.error('You must be logged in');
      return { success: false, error: 'Not logged in' };
    }

    // Ensure user exists in Supabase
    await initializeSupabaseUser(mongoUser);

    const result = await getOrCreateConversation(mongoUser.id, friendId);

    if (!result.success) {
      toast.error(result.error?.message || result.error || 'Failed to open conversation');
    }

    return result;
  }, [initializeSupabaseUser]);

  /**
   * Send a message in a conversation
   */
  const sendMessage = useCallback(async (conversationId, mongoUser, message) => {
    if (!mongoUser?.id) {
      toast.error('You must be logged in');
      return { success: false, error: 'Not logged in' };
    }

    if (!message?.trim()) {
      return { success: false, error: 'Message cannot be empty' };
    }

    const result = await sendMessageService(conversationId, mongoUser.id, message.trim());

    if (!result.success) {
      toast.error(result.error?.message || result.error || 'Failed to send message');
    }

    return result;
  }, []);

  /**
   * Fetch messages for a conversation
   */
  const fetchMessages = useCallback(async (conversationId) => {
    if (!conversationId) return { success: false, error: 'Conversation ID required' };

    const result = await getMessages(conversationId);
    return result;
  }, []);

  /**
   * Mark messages as read
   */
  const markAsRead = useCallback(async (conversationId, userId) => {
    if (!conversationId || !userId) return { success: false, error: 'Missing parameters' };

    const result = await markMessagesAsRead(conversationId, userId);
    return result;
  }, []);

  /**
   * Subscribe to messages in a conversation
   */
  const subscribeToConversation = useCallback((conversationId, onInsert, onUpdate) => {
    if (!conversationId) return null;

    const channel = subscribeToMessages(conversationId, onInsert, onUpdate);
    return channel;
  }, []);

  /**
   * Subscribe to incoming friend requests for a user
   */
  const subscribeToUserFriendRequests = useCallback((userId, callback) => {
    if (!userId) return null;

    // Unsubscribe from previous channel if exists
    if (friendRequestsChannel) {
      unsubscribeChannel(friendRequestsChannel);
    }

    const channel = subscribeToFriendRequests(userId, callback);
    setFriendRequestsChannel(channel);
    return channel;
  }, [friendRequestsChannel]);

  /**
   * Unsubscribe from a channel
   */
  const unsubscribe = useCallback((channel) => {
    unsubscribeChannel(channel);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (friendRequestsChannel) {
        unsubscribeChannel(friendRequestsChannel);
      }
    };
  }, [friendRequestsChannel]);

  const value = {
    // State
    pendingRequests,
    friends,
    loading,
    supabase,
    
    // User
    initializeSupabaseUser,
    
    // Friend Requests
    sendFriendRequest,
    fetchFriendRequests,
    acceptFriendRequest,
    rejectFriendRequest,
    
    // Friends
    fetchFriends,
    
    // Conversations & Messages
    openConversation,
    sendMessage,
    fetchMessages,
    markAsRead,
    
    // Subscriptions
    subscribeToConversation,
    subscribeToUserFriendRequests,
    unsubscribe
  };

  return (
    <SupabaseContext.Provider value={value}>
      {children}
    </SupabaseContext.Provider>
  );
};

export default SupabaseContext;
